import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import advisorStyles from './advisor.module.css';
import styles from './calculator.module.css';

/**
 * Inline SVG charts of the advisor view („Ihr Ergebnis über 30 Jahre", D-391): no chart library, a responsive
 * viewBox, colors from tokens through the CSS modules, axis labels in whole euros (thousands as "T€" like Nico's axis).
 */

const WIDTH = 1040;
const HEIGHT = 300;
const PAD = { top: 12, right: 12, bottom: 28, left: 56 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

/** 158499_00 → "158 T€", 950_00 → "950 €" — compact axis label. */
export function axisLabel(cents: number): string {
  const euros = Math.round(cents / 100);
  if (Math.abs(euros) >= 1000) return `${formatInteger(Math.round(euros / 1000))}\u00a0T€`;
  return `${formatInteger(euros)}\u00a0€`;
}

/** A "nice" axis maximum and step for 4 grid lines. */
export function niceScale(maxCents: number): { max: number; step: number } {
  if (maxCents <= 0) return { max: 100_00, step: 25_00 };
  const raw = maxCents / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  const step = nice * magnitude;
  return { max: step * 4, step };
}

export type SignedTone = 'cumulative' | 'value' | 'debt';

const SIGNED_STROKE: Record<SignedTone, string | undefined> = {
  cumulative: advisorStyles.lineCumulative,
  value: advisorStyles.lineValue,
  debt: advisorStyles.lineDebt,
};

export interface SignedSeries {
  label: string;
  tone: SignedTone;
  /** Cents per year 1…N. */
  values: readonly number[];
  dashed?: boolean | undefined;
}

export interface AxisMark {
  /** 0-based index into the series. */
  index: number;
  label: string;
}

/** Axis bounds around 0 with a "nice" step: at least one grid line below 0 when the data goes negative. */
export function signedScale(minCents: number, maxCents: number): { min: number; max: number; step: number } {
  const low = Math.min(0, minCents);
  const high = Math.max(0, maxCents);
  const { step } = niceScale(high - low);
  return { min: Math.floor(low / step) * step, max: Math.ceil(high / step) * step || step, step };
}

/**
 * Lines over the years with negative values (cumulative cash flow) or a filled band between two series
 * (property value above remaining debt = „Ihr Vermögen"), a zero line and an optional marker at one year.
 */
export function SignedLineChart({
  series,
  label,
  marks,
  marker,
  band,
}: {
  series: SignedSeries[];
  label: string;
  marks: AxisMark[];
  /** 0-based index of the highlighted year. */
  marker?: number | undefined;
  /** Fills the area between the first (upper) and the second (lower) series. */
  band?: boolean | undefined;
}) {
  const count = Math.max(...series.map((entry) => entry.values.length), 1);
  const all = series.flatMap((entry) => entry.values);
  const { min, max, step } = signedScale(Math.min(...all, 0), Math.max(...all, 0));
  const span = max - min || 1;
  const sx = (index: number) => PAD.left + (count <= 1 ? 0 : (index / (count - 1)) * PLOT_W);
  const sy = (value: number) => PAD.top + PLOT_H - ((value - min) / span) * PLOT_H;
  const line = (values: readonly number[]) =>
    values
      .map((value, index) => `${index === 0 ? 'M' : 'L'}${sx(index).toFixed(1)},${sy(value).toFixed(1)}`)
      .join(' ');
  const grid = [];
  for (let value = min; value <= max + 0.5; value += step) grid.push(value);
  const upper = series[0];
  const lower = series[1];
  const bandPath =
    band && upper && lower
      ? `${line(upper.values)} ${[...lower.values]
          .map((value, index) => ({ value, index }))
          .reverse()
          .map(({ value, index }) => `L${sx(index).toFixed(1)},${sy(value).toFixed(1)}`)
          .join(' ')} Z`
      : null;
  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className={styles.chart} role="img" aria-label={label}>
      {grid.map((value) => (
        <g key={value}>
          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={sy(value)}
            y2={sy(value)}
            className={value === 0 ? advisorStyles.zeroLine : styles.chartGrid}
          />
          <text x={PAD.left - 8} y={sy(value) + 4} textAnchor="end" className={styles.chartTick}>
            {axisLabel(value)}
          </text>
        </g>
      ))}
      {marks.map((mark) => (
        <text
          key={mark.index}
          x={sx(mark.index)}
          y={HEIGHT - 8}
          textAnchor={mark.index === 0 ? 'start' : mark.index === count - 1 ? 'end' : 'middle'}
          className={styles.chartTick}
        >
          {mark.label}
        </text>
      ))}
      {bandPath ? <path d={bandPath} className={advisorStyles.bandWealth} /> : null}
      {marker !== undefined ? (
        <line
          x1={sx(marker)}
          x2={sx(marker)}
          y1={PAD.top}
          y2={PAD.top + PLOT_H}
          className={advisorStyles.markerLine}
        />
      ) : null}
      {series.map((entry) => (
        <path
          key={entry.label}
          d={line(entry.values)}
          className={clsx(styles.chartLine, SIGNED_STROKE[entry.tone], entry.dashed && styles.chartDashed)}
        />
      ))}
      {marker !== undefined
        ? series.map((entry) => {
            const value = entry.values[marker];
            return value === undefined ? null : (
              <circle
                key={`${entry.label}-marker`}
                cx={sx(marker)}
                cy={sy(value)}
                r={4.5}
                className={clsx(advisorStyles.markerDot, SIGNED_STROKE[entry.tone])}
              />
            );
          })
        : null}
    </svg>
  );
}
